// src/services/api/utils/concurrencyLimit.ts
//
// Tiny pLimit-style helper so we don't take a new dependency for a 30-line
// queue. Used to cap fanout against /gatherings/search — see ENG-933.

/**
 * Returns a function that runs at most `concurrency` async tasks in flight.
 * Additional callers queue FIFO until a slot frees up.
 *
 * The optional `signal` lets callers cancel queued work *before* it starts.
 * If the signal is already aborted, the returned promise rejects immediately
 * and no slot is taken. If the signal aborts while queued, the caller is
 * removed from the queue and rejected with an AbortError. The signal is NOT
 * applied to running work — pass it to your underlying fetch/request so the
 * runtime can cancel mid-flight (fetch does this natively via RequestInit).
 */
export function createLimiter(concurrency: number) {
  if (concurrency < 1) {
    throw new Error(`concurrency must be >= 1, got ${concurrency}`);
  }

  let active = 0;
  const queue: Array<() => void> = [];

  const releaseSlot = (): void => {
    active--;
    queue.shift()?.();
  };

  return async function run<T>(
    fn: () => Promise<T>,
    signal?: AbortSignal
  ): Promise<T> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    if (active >= concurrency) {
      // Wait for a slot, but bail if the signal fires while queued.
      await new Promise<void>((resolve, reject) => {
        const wakeup = (): void => {
          signal?.removeEventListener('abort', onAbort);
          resolve();
        };
        const onAbort = (): void => {
          const idx = queue.indexOf(wakeup);
          if (idx !== -1) {
            queue.splice(idx, 1);
          }
          reject(new DOMException('Aborted', 'AbortError'));
        };
        queue.push(wakeup);
        signal?.addEventListener('abort', onAbort, { once: true });
      });
    }

    active++;
    try {
      return await fn();
    } finally {
      releaseSlot();
    }
  };
}

/**
 * True if `err` is an AbortError thrown by an AbortSignal-aware operation
 * (fetch, the limiter, etc.). Use to swallow expected aborts on cleanup.
 */
export const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';
